import * as React from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Button,
    Typography,
    Box,
    Grid,
    Switch,
    Alert,
    IconButton,
    Stepper,
    Step,
    StepLabel,
    TextField,
    FormControlLabel,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Divider,
    Collapse,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
} from '@mui/material'
import {
    Close as CloseIcon,
    CloudUpload as CloudUploadIcon,
    Description as FileIcon,
    Delete as DeleteIcon,
    ExpandMore,
    ExpandLess,
    CheckCircle,
} from '@mui/icons-material'
import { useDropzone } from 'react-dropzone'
import { useZustand } from '../../hooks/useZustand'
import {
    useContextsStore,
    selectOwnedContexts,
} from '../../stores/useContextsStore'
import usePageInfo from '../../hooks/usePageInfo'
import { SubImportContext } from './SubImportContext'
import { DialogsContext } from '../../DialogsContext'
import { UserContext } from '../../UserContext'
import JSONFieldSelector from './JSONFieldSelector'
import EditableSelect from '../EditableSelect'
import { slugifyString } from '../../utils'
import axios from 'axios'
import { importErrorMessage } from './importError'

const addPrivateStatementsSelector = state => state.addPrivateStatementsToStore

const steps = ['Upload', 'Select', 'Filter', 'Import']

function isJsonOrJsonlFile(file) {
    if (!file || !file.name) return false
    const name = file.name.toLowerCase()
    const mime = (file.type || '').toLowerCase()
    const jsonlByName =
        name.endsWith('.json') ||
        name.endsWith('.jsonl') ||
        name.endsWith('.ndjson')
    return (
        mime === 'application/json' ||
        mime === 'application/x-ndjson' ||
        mime === 'application/jsonlines' ||
        mime === 'application/ndjson' ||
        (mime === 'text/plain' && jsonlByName) ||
        (mime === 'application/octet-stream' && jsonlByName) ||
        jsonlByName
    )
}

function stripJsonLikeExtension(fileName) {
    return fileName.replace(/\.(json|jsonl|ndjson)$/i, '')
}

// Helper function to extract all field paths from JSON structure
function extractFieldPaths(obj, prefix = '') {
    const paths = new Set()

    const traverse = (current, currentPath) => {
        if (current === null || current === undefined) return

        if (Array.isArray(current)) {
            // For arrays, traverse the first element to get structure
            if (current.length > 0 && typeof current[0] === 'object') {
                traverse(current[0], currentPath + '[*]')
            } else {
                // Array of primitives
                paths.add(currentPath)
            }
        } else if (typeof current === 'object') {
            // For objects, traverse all keys
            Object.keys(current).forEach(key => {
                const newPath = currentPath ? `${currentPath}.${key}` : key

                // Add intermediate path for object fields
                if (typeof current[key] !== 'object' || current[key] === null) {
                    paths.add(newPath)
                }

                traverse(current[key], newPath)
            })
        }
    }

    traverse(obj, prefix)
    return Array.from(paths).sort()
}

// Helper function to extract value from nested path
function getValueFromPath(obj, path) {
    const keys = path.split(/\.(?![^\[]*\])/) // Split by dots but not within brackets
    let value = obj

    for (let i = 0; i < keys.length; i++) {
        const key = keys[i]
        if (key.includes('[')) {
            // Handle array notation like field[0] or field[*]
            const match = key.match(/^([^\[]+)\[([^\]]+)\](.*)$/)
            if (match) {
                const [, fieldName, index, rest] = match
                value = value?.[fieldName]

                if (!value) return ''

                if (index === '*') {
                    if (!Array.isArray(value)) {
                        return ''
                    }
                    const tailFromKeys = keys.slice(i + 1).join('.')
                    let remainder = ''
                    if (rest) {
                        remainder = rest.startsWith('.')
                            ? rest.slice(1)
                            : rest
                    }
                    if (tailFromKeys) {
                        remainder = remainder
                            ? `${remainder}.${tailFromKeys}`
                            : tailFromKeys
                    }

                    if (remainder) {
                        return value
                            .map(item => getValueFromPath(item, remainder))
                            .filter(v => v != null && v !== '')
                            .join(' ')
                    }

                    return value
                        .map(item =>
                            typeof item === 'object'
                                ? JSON.stringify(item)
                                : item
                        )
                        .filter(v => v != null)
                        .join(' ')
                } else {
                    const idx = parseInt(index, 10)
                    if (!isNaN(idx) && Array.isArray(value)) {
                        value = value[idx]
                        const tailFromKeys = keys.slice(i + 1).join('.')
                        let remainder = ''
                        if (rest && rest.startsWith('.')) {
                            remainder = rest.slice(1)
                        }
                        if (tailFromKeys) {
                            remainder = remainder
                                ? `${remainder}.${tailFromKeys}`
                                : tailFromKeys
                        }
                        if (remainder) {
                            return getValueFromPath(value, remainder)
                        }
                    } else {
                        return ''
                    }
                }
            }
        } else {
            value = value?.[key]
        }

        if (value === undefined || value === null) {
            return ''
        }
    }

    return typeof value === 'object' ? JSON.stringify(value) : String(value)
}

// Keep in sync with lib/imports/json.js collectClaudeMessageContentFallback
function collectClaudeMessageContentFallback(item) {
    const msg = item?.message
    if (!msg || typeof msg !== 'object') return ''
    const c = msg.content
    if (typeof c === 'string') return c.trim()
    if (!Array.isArray(c)) return ''
    const parts = []
    for (const block of c) {
        if (!block || typeof block !== 'object') continue
        if (typeof block.text === 'string' && block.text.trim()) {
            parts.push(block.text.trim())
        }
        if (typeof block.thinking === 'string' && block.thinking.trim()) {
            parts.push(block.thinking.trim())
        }
        const inp = block.input
        if (inp && typeof inp === 'object') {
            if (typeof inp.prompt === 'string' && inp.prompt.trim()) {
                parts.push(inp.prompt.trim())
            }
            if (typeof inp.description === 'string' && inp.description.trim()) {
                parts.push(inp.description.trim())
            }
        }
    }
    return parts.join(' ')
}

export default function JSONFileImportDialog({ presetContext, open, onClose }) {
    const { label, location } = usePageInfo()
    const { presetFiles, closeImportDialog } = React.useContext(DialogsContext)
    const { username } = React.useContext(UserContext)

    const addPrivateStatementsToStore = useZustand(addPrivateStatementsSelector)
    const contexts = useContextsStore(selectOwnedContexts)
    const contextNames = contexts.map(c => c.name)
    const [activeStep, setActiveStep] = React.useState(0)
    const [loading, setLoading] = React.useState(false)
    const [error, setError] = React.useState('')
    const [success, setSuccess] = React.useState('')

    // File state
    const [jsonFile, setJsonFile] = React.useState(null)
    const [jsonData, setJsonData] = React.useState(null)
    const [availableFields, setAvailableFields] = React.useState([])

    // Field selection state
    const [selectedFields, setSelectedFields] = React.useState([])
    const [filterFields, setFilterFields] = React.useState([])
    const [fieldPreview, setFieldPreview] = React.useState({})

    const [advancedSettings, setAdvancedSettings] = React.useState(false)

    const [advancedFilterSettings, setAdvancedFilterSettings] = React.useState(
        false
    )

    // Import configuration
    const [graphName, setGraphName] = React.useState('')
    const [fileName, setFileName] = React.useState('')
    const [contextList, setContextList] = React.useState(presetContext || '')
    const [preprocessText, setPreprocessText] = React.useState(true)
    const [addColumnNameToFilter, setAddColumnNameToFilter] = React.useState(
        true
    )
    const [columnForTimestamp, setColumnForTimestamp] = React.useState('none')
    const [universalTag, setUniversalTag] = React.useState('')
    const [splitByColumn, setSplitByColumn] = React.useState('none')
    const [superlinkedColumn, setSuperlinkedColumn] = React.useState('none')
    const [modifyAnalyzedText, setModifyAnalyzedText] = React.useState('none')
    const [replaceEntities, setReplaceEntities] = React.useState(false)

    // Preview state
    const [showPreview, setShowPreview] = React.useState(false)
    const [processedPreview, setProcessedPreview] = React.useState([])

    React.useEffect(() => {
        if (presetContext) {
            setContextList(presetContext)
            setGraphName(presetContext)
        }
    }, [presetContext])

    React.useEffect(() => {
        const existPresetFiles =
            presetFiles && presetFiles.length > 0 ? true : false
        if (existPresetFiles) handleUploadedFile([presetFiles[0]])
    }, [])

    // Update preview when fields change
    React.useEffect(() => {
        if (jsonData && selectedFields.length > 0) {
            updatePreview()
        }
    }, [selectedFields, filterFields, jsonData])

    React.useEffect(() => {
        setUniversalTag(`file: ${slugifyString(fileName).slice(0, 32)}`)
    }, [fileName])

    const updatePreview = () => {
        const preview = []
        const dataArray = Array.isArray(jsonData) ? jsonData : [jsonData]

        const hasArrayFields = selectedFields.some(field =>
            field.includes('[*]')
        )
        const multipleRoots = dataArray.length > 1

        // Single root + [*]: expand first array (e.g. one blob with organic_results[*])
        const expandArrayPreview =
            hasArrayFields && !multipleRoots && dataArray[0]

        if (expandArrayPreview) {
            const arrayPath = selectedFields
                .find(f => f.includes('[*]'))
                ?.split('[*]')[0]

            if (arrayPath) {
                const arrayData = arrayPath
                    .split('.')
                    .reduce((obj, key) => obj?.[key], dataArray[0])

                if (Array.isArray(arrayData)) {
                    arrayData.slice(0, 5).forEach((arrayItem, index) => {
                        const textParts = []
                        const filters = {}

                        selectedFields.forEach(field => {
                            if (field.startsWith(`${arrayPath}[*]`)) {
                                const subPath = field.replace(
                                    `${arrayPath}[*].`,
                                    ''
                                )
                                const value = subPath
                                    ? getValueFromPath(arrayItem, subPath)
                                    : arrayItem
                                if (value) textParts.push(value)
                            }
                        })

                        filterFields.forEach(field => {
                            if (field.startsWith(`${arrayPath}[*]`)) {
                                const subPath = field.replace(
                                    `${arrayPath}[*].`,
                                    ''
                                )
                                const value = subPath
                                    ? getValueFromPath(arrayItem, subPath)
                                    : arrayItem
                                if (value) {
                                    filters[subPath] = value
                                }
                            }
                        })

                        const text = textParts.filter(t => t).join(' ')
                        preview.push({
                            id: index,
                            text:
                                text.substring(0, 200) +
                                (text.length > 200 ? '...' : ''),
                            filters,
                        })
                    })
                }
            }
        }

        // NDJSON / many roots, or no [*], or expand preview produced nothing
        if (preview.length === 0) {
            dataArray.slice(0, 5).forEach((item, index) => {
                const textParts = selectedFields.map(field =>
                    getValueFromPath(item, field)
                )
                let text = textParts.filter(t => t).join(' ')
                const usesMessageContent = selectedFields.some(
                    f =>
                        typeof f === 'string' &&
                        f.includes('message.content')
                )
                if (
                    multipleRoots &&
                    hasArrayFields &&
                    usesMessageContent &&
                    !String(text || '').trim()
                ) {
                    const fb = collectClaudeMessageContentFallback(item)
                    if (fb) text = fb
                }

                const filters = {}
                filterFields.forEach(field => {
                    const value = getValueFromPath(item, field)
                    if (value) {
                        filters[field] = value
                    }
                })

                preview.push({
                    id: index,
                    text:
                        text.substring(0, 200) +
                        (text.length > 200 ? '...' : ''),
                    filters,
                })
            })
        }

        setProcessedPreview(preview)
    }

    const handleUploadedFile = React.useCallback(
        acceptedFiles => {
            const file = acceptedFiles[0]
            if (file && isJsonOrJsonlFile(file)) {
                setJsonFile(file)
                setError('')

                const reader = new FileReader()
                reader.onload = e => {
                    try {
                        let data
                        const content = e.target.result

                        // Try standard JSON parsing first
                        try {
                            data = JSON.parse(content)
                        } catch (parseError) {
                            // If that fails, try NDJSON (newline-delimited JSON)
                            const lines = content.trim().split('\n')
                            if (lines.length > 1) {
                                console.log(`Processing NDJSON file with ${lines.length} lines`)

                                // Parse each line as separate JSON object
                                data = lines
                                    .filter(line => line.trim())
                                    .map((line, index) => {
                                        try {
                                            return JSON.parse(line)
                                        } catch (lineError) {
                                            console.error(`Error parsing line ${index + 1}:`, lineError.message)
                                            return null
                                        }
                                    })
                                    .filter(obj => obj !== null)

                                console.log(`Successfully parsed ${data.length} records for preview`)
                            } else {
                                // Re-throw the original error if it's not NDJSON
                                throw parseError
                            }
                        }

                        setJsonData(data)

                        // Extract available fields
                        const dataArray = Array.isArray(data) ? data : [data]
                        if (dataArray.length > 0) {
                            // Build field list from multiple records (important for NDJSON
                            // where objects may have different shapes across lines).
                            const fieldsSet = new Set()
                            dataArray.slice(0, 200).forEach(item => {
                                extractFieldPaths(item).forEach(field =>
                                    fieldsSet.add(field)
                                )
                            })
                            const fields = Array.from(fieldsSet).sort()
                            setAvailableFields(fields)

                            // Generate preview for each field
                            const preview = {}
                            fields.forEach(field => {
                                const samples = dataArray
                                    .slice(0, 3)
                                    .map(item => getValueFromPath(item, field))
                                    .filter(v => v)
                                if (samples.length > 0) {
                                    preview[field] = samples
                                }
                            })
                            setFieldPreview(preview)
                        }

                        if (!graphName) {
                            const base = stripJsonLikeExtension(file.name)
                            setGraphName(base)
                            setFileName(base)
                        }
                    } catch (err) {
                        setError(
                            `Invalid JSON or JSON Lines file: ${err.message || 'Parse error'}`
                        )
                        setJsonFile(null)
                        setJsonData(null)
                        setAvailableFields([])
                    }
                }
                reader.readAsText(file)
            } else {
                setError(
                    'Please upload a .json file or a JSON Lines file (.jsonl / .ndjson: one JSON object per line)'
                )
            }
        },
        [graphName]
    )

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: handleUploadedFile,
        accept: {
            'application/json': ['.json', '.jsonl', '.ndjson'],
            'application/x-ndjson': ['.jsonl', '.ndjson'],
            'application/jsonlines': ['.jsonl', '.ndjson'],
            'application/ndjson': ['.jsonl', '.ndjson'],
            'text/plain': ['.jsonl', '.ndjson'],
        },
        multiple: false,
    })

    const handleFieldToggle = field => {
        setSelectedFields(prev =>
            prev.includes(field)
                ? prev.filter(f => f !== field)
                : [...prev, field]
        )
    }

    const handleFilterToggle = field => {
        setFilterFields(prev =>
            prev.includes(field)
                ? prev.filter(f => f !== field)
                : [...prev, field]
        )
    }

    const handleNext = () => {
        setActiveStep(prevStep => prevStep + 1)
    }

    const handleBack = () => {
        setActiveStep(prevStep => prevStep - 1)
    }

    const canProceedToNext = () => {
        switch (activeStep) {
            case 0:
                return jsonFile && jsonData && availableFields.length > 0
            case 1:
                return selectedFields.length > 0
            case 2:
                return true // Filter fields are optional
            case 3:
                return graphName.trim() !== ''
            default:
                return false
        }
    }

    const handleSubmit = async () => {
        if (!jsonFile || !jsonData) {
            setError('Please select a JSON file')
            return
        }

        setLoading(true)
        setError('')

        try {
            console.log('Starting JSON import with file:', jsonFile?.name, 'size:', jsonFile?.size)
            const formData = new FormData()
            formData.append('uploadedFile', jsonFile)
            formData.append('context', contextList || graphName)
            formData.append('graphName', graphName)
            formData.append('doNotSaveGraph', 'false')
            formData.append('selectedFields', JSON.stringify(selectedFields))
            formData.append('filterFields', JSON.stringify(filterFields))
            formData.append('preprocessText', String(preprocessText))
            formData.append(
                'addColumnNameToFilter',
                String(addColumnNameToFilter)
            )
            formData.append('namelink', universalTag ? universalTag : '')
            formData.append('includenamelink', universalTag ? '1' : '')
            formData.append(
                'timestamps',
                columnForTimestamp !== 'none' ? columnForTimestamp : ''
            )
            formData.append(
                'titlefield',
                splitByColumn !== 'none' ? splitByColumn : ''
            )
            formData.append(
                'categoriesAsMentions',
                superlinkedColumn !== 'none' ? superlinkedColumn : ''
            )
            formData.append(
                'modifyAnalyzedText',
                modifyAnalyzedText !== 'none' ? modifyAnalyzedText : ''
            )
            if (replaceEntities) {
                formData.append('replaceEntities', 'true')
            }

            console.log('Sending form data to /import/json...')
            axios
                .post('/import/json', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
                .then(response => {
                    setLoading(false)
                    if (response.data.redirectUrl) {
                        // If you're using window.location
                        window.location.href = response.data.redirectUrl
                    } else {
                        if (response.data.error) {
                            setError(
                                typeof response.data.error == 'object'
                                    ? response.data.error.error ||
                                      JSON.stringify(response.data.error)
                                    : response.data.error
                            )
                        }
                        if (response.data.entryuid) {
                            const postedby = username
                            const res = response.data

                            // Handle private mode statements
                            if (doNotSaveGraph && res.statements) {
                                // Private mode: add statements to Zustand store without saving to DB
                                const entryuids = JSON.parse(res.entryuid)
                                const statementHashtags =
                                    res.statementHashtags || {}
                                addPrivateStatementsToStore(
                                    res.statements,
                                    graphName,
                                    entryuids,
                                    statementHashtags
                                )
                            }

                            if (
                                window.myEventEmitter &&
                                myEventEmitter._events['chatMessage']
                            ) {
                                myEventEmitter.emit('chatMessage', {
                                    source: null,
                                    postedby: postedby,
                                    entryuid: JSON.parse(res.entryuid),
                                    entrytext: JSON.parse(res.entrytext),
                                    entryname: JSON.parse(res.entryname),
                                    tags: JSON.parse(res.tags),
                                    timestamp: JSON.parse(res.timestamp),
                                    graph: res.graph,
                                    stimulateVariability: false,
                                    message: res.message,
                                    statementHashtags: res.statementHashtags || {},
                                    doNotSaveGraph: doNotSaveGraph,
                                })
                                closeImportDialog()
                            }
                        }
                    }
                })
                .catch(error => {
                    // Handle the error here
                    console.error('Received the following error', error)
                    setError(importErrorMessage(error))
                    setLoading(false)
                })
        } catch (err) {
            setError(`Import failed: ${err.message}`)
            setLoading(false)
        }
    }

    const handleClose = () => {
        if (!loading) {
            onClose(false)
        }
    }

    const renderUploadStep = () => (
        <Box sx={{ padding: 1 }}>
            <Typography variant="h6" gutterBottom>
                1. Upload JSON or JSON Lines
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Upload a .json file (single document or an array of objects) or
                a .jsonl / .ndjson file (newline-delimited JSON: one JSON object
                per line). Mixed
                shapes across lines are supported; field paths are merged for
                selection.
            </Typography>

            <Box
                {...getRootProps()}
                sx={{
                    border: '2px dashed',
                    borderColor: isDragActive ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: isDragActive
                        ? 'action.hover'
                        : 'background.paper',
                    transition: 'all 0.3s',
                    '&:hover': {
                        backgroundColor: 'action.hover',
                    },
                }}
            >
                <input {...getInputProps()} />
                <CloudUploadIcon
                    sx={{ fontSize: 48, color: 'action.active', mb: 2 }}
                />
                <Typography variant="h6">
                    {isDragActive
                        ? 'Drop the file here'
                        : 'Drag & drop here, or click to browse'}
                </Typography>
                <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mt: 1 }}
                >
                    Supported: .json, .jsonl, .ndjson
                </Typography>
            </Box>

            {jsonFile && (
                <Box sx={{ mt: 3 }}>
                    <Paper sx={{ p: 2 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <FileIcon
                                    sx={{ mr: 2, color: 'primary.main' }}
                                />
                                <Box>
                                    <Typography variant="subtitle1">
                                        {jsonFile.name}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        {Array.isArray(jsonData)
                                            ? jsonData.length
                                            : 1}{' '}
                                        items • {availableFields.length} fields
                                        detected
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton
                                onClick={() => {
                                    setJsonFile(null)
                                    setJsonData(null)
                                    setAvailableFields([])
                                    setSelectedFields([])
                                    setFilterFields([])
                                }}
                                size="small"
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    </Paper>
                </Box>
            )}
        </Box>
    )

    const renderFieldSelectionStep = () => (
        <JSONFieldSelector
            jsonData={jsonData}
            selectedFields={selectedFields}
            onFieldToggle={handleFieldToggle}
            title="2. Select Fields to Analyze"
            helperText="Choose which JSON fields contain the text data you want to analyze. Navigate through the tree structure to select specific fields, arrays, or nested objects. Multiple selections will be combined for analysis."
            onPreviewClick={(path, value) => {
                // Show a preview dialog or alert with the value
                console.log('Preview:', path, value)
            }}
        />
    )

    const renderFilterSelectionStep = () => (
        <Box sx={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
            <JSONFieldSelector
                jsonData={jsonData}
                selectedFields={filterFields}
                onFieldToggle={handleFilterToggle}
                title="3. Select Filter Fields (Optional)"
                helperText="Choose fields to use as filter tags in your graph. These will help you filter and categorize your data (e.g., category, status, type). Best used with fields containing categorical data."
                onPreviewClick={(path, value) => {
                    console.log('Filter preview:', path, value)
                }}
            />
            <Grid container spacing={8} sx={{ padding: 1 }}>
                <Grid item xs={12} sm={6}>
                    <FormControl
                        sx={{
                            marginTop: 1,
                            paddingRight: 2,
                        }}
                    >
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={addColumnNameToFilter}
                                    onChange={event =>
                                        setAddColumnNameToFilter(
                                            event.target.checked
                                        )
                                    }
                                />
                            }
                            label="Add field name to filter tags"
                        />
                    </FormControl>
                    <FormHelperText>
                        (e.g., 'status:active' instead of just 'active')
                    </FormHelperText>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            marginTop: 2,
                            marginBottom: 2,
                        }}
                        spacing={2}
                    >
                        <FormControl>
                            <InputLabel id="columnForTimestamp-label">
                                Column to use for the timestamp:
                            </InputLabel>
                            <Select
                                labelId="columnForTimestamp-label"
                                id="columnForTimestamp"
                                value={columnForTimestamp}
                                onChange={event =>
                                    setColumnForTimestamp(event.target.value)
                                }
                                label="Column to use for the timestamp:"
                            >
                                <MenuItem value={'none'} key="0">
                                    None
                                </MenuItem>
                                {availableFields.map((colName, index) => (
                                    <MenuItem key={index + 1} value={colName}>
                                        {colName}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText sx={{ marginTop: 1 }}>
                                Preferred format: mm/dd/yyyy. If you select a
                                column, you can use it to filter results by
                                different timeframes.
                            </FormHelperText>
                        </FormControl>
                        <FormControl fullWidth sx={{ marginTop: 4 }}>
                            <EditableSelect
                                id="universalTag"
                                label={'Tag all data with'}
                                initialValue={universalTag}
                                onChange={name =>
                                    setUniversalTag(slugifyString(name))
                                }
                                placeholder={``}
                            />
                            <FormHelperText sx={{ marginTop: 1 }}>
                                Tag all imported data with this value, so you
                                can use it as a filter. Leave empty for none.
                            </FormHelperText>
                        </FormControl>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    )

    const renderConfigurationStep = () => (
        <Box sx={{ padding: 1 }}>
            <Typography variant="h6" gutterBottom>
                4. Configure Import
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Give your graph a name and review your import settings.
            </Typography>

            <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}
            >
                <Grid container spacing={8}>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <EditableSelect
                                selectItems={contexts}
                                label={'Graph Name'}
                                initialValue={graphName}
                                onChange={name => setGraphName(name)}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                mt: 2,
                            }}
                        >
                            <FormControl fullWidth>
                                <InputLabel>
                                    Build Knowledge Graph From
                                </InputLabel>
                                <Select
                                    value={modifyAnalyzedText}
                                    onChange={e =>
                                        setModifyAnalyzedText(e.target.value)
                                    }
                                    label="Build Knowledge Graph From"
                                >
                                    <MenuItem value="none">
                                        Words used in the text
                                    </MenuItem>
                                    <MenuItem value="detectEntities">
                                        Words and entities identified in the
                                        text
                                    </MenuItem>
                                    <MenuItem value="extractEntitiesOnly">
                                        Only the entities identified in the text
                                    </MenuItem>
                                    <MenuItem value="convertToEntities">
                                        Tag the whole cell text as an entity
                                    </MenuItem>
                                </Select>
                                <FormHelperText>
                                    Choose how to process text for knowledge
                                    graph construction
                                </FormHelperText>
                            </FormControl>

                            {modifyAnalyzedText !== 'none' && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={replaceEntities}
                                            onChange={e =>
                                                setReplaceEntities(
                                                    e.target.checked
                                                )
                                            }
                                        />
                                    }
                                    label="Replace entities with root concepts"
                                    sx={{ mt: 1 }}
                                />
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            <Button
                onClick={() => setShowPreview(!showPreview)}
                endIcon={showPreview ? <ExpandLess /> : <ExpandMore />}
                size="small"
                sx={{ mb: 2 }}
            >
                Preview Processed Data
            </Button>

            <Collapse in={showPreview}>
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    Text (from selected fields)
                                </TableCell>
                                {filterFields.length > 0 && (
                                    <TableCell>Filter Tags</TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {processedPreview.map(item => (
                                <TableRow key={item.id}>
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontSize: '0.85rem' }}
                                        >
                                            {item.text}
                                        </Typography>
                                    </TableCell>
                                    {filterFields.length > 0 && (
                                        <TableCell>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: 0.5,
                                                }}
                                            >
                                                {Object.entries(
                                                    item.filters
                                                ).map(([key, value]) => (
                                                    <Chip
                                                        key={key}
                                                        label={
                                                            addColumnNameToFilter
                                                                ? `${key}:${value}`
                                                                : value
                                                        }
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                ))}
                                            </Box>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Typography variant="caption" color="textSecondary">
                    Showing first 5 of{' '}
                    {Array.isArray(jsonData) ? jsonData.length : 1} items
                </Typography>
            </Collapse>

            <Paper sx={{ p: 2, backgroundColor: 'action.hover', mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                    Import Summary
                </Typography>
                <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">
                        • <strong>Items to import:</strong>{' '}
                        {(() => {
                            const rootCount = Array.isArray(jsonData)
                                ? jsonData.length
                                : 1
                            const hasArrayFields = selectedFields.some(field =>
                                field.includes('[*]')
                            )
                            // Matches lib/imports/json.js processJSONData: multiple
                            // roots → one row per root ([*] merged inside each).
                            if (hasArrayFields && rootCount > 1) {
                                return rootCount
                            }
                            if (hasArrayFields && rootCount === 1) {
                                const arrayPath = selectedFields
                                    .find(f => f.includes('[*]'))
                                    ?.split('[*]')[0]
                                if (arrayPath) {
                                    const data = Array.isArray(jsonData)
                                        ? jsonData[0]
                                        : jsonData
                                    const arrayData = arrayPath
                                        .split('.')
                                        .reduce((obj, key) => obj?.[key], data)
                                    if (Array.isArray(arrayData)) {
                                        return arrayData.length
                                    }
                                }
                            }
                            return rootCount
                        })()}
                    </Typography>
                    {Array.isArray(jsonData) &&
                        jsonData.length > 1 &&
                        selectedFields.some(
                            f =>
                                typeof f === 'string' &&
                                f.includes('message.content')
                        ) && (
                            <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{ display: 'block', mt: 0.5, mb: 0.5 }}
                            >
                                One row per line. Paths such as input.prompt may
                                exist only on some blocks (e.g. Agent); when the
                                selected path is empty for a line, we add other
                                message text (user string, text blocks,
                                thinking, tool descriptions) so most lines
                                import.
                            </Typography>
                        )}
                    <Typography variant="body2">
                        • <strong>Text fields:</strong>{' '}
                        {selectedFields.join(', ')}
                    </Typography>
                    {filterFields.length > 0 && (
                        <Typography variant="body2">
                            • <strong>Filter fields:</strong>{' '}
                            {filterFields.join(', ')}
                        </Typography>
                    )}
                    <Typography variant="body2">
                        • <strong>Graph name:</strong>{' '}
                        {graphName || '(not set)'}
                    </Typography>
                    {contextList && (
                        <Typography variant="body2">
                            • <strong>Context:</strong> {contextList}
                        </Typography>
                    )}
                    {columnForTimestamp !== 'none' && (
                        <Typography variant="body2">
                            • <strong>Timestamp field:</strong>{' '}
                            {columnForTimestamp}
                        </Typography>
                    )}
                    {universalTag && (
                        <Typography variant="body2">
                            • <strong>Universal tag:</strong> {universalTag}
                        </Typography>
                    )}
                    {splitByColumn !== 'none' && (
                        <Typography variant="body2">
                            • <strong>Split by column:</strong> {splitByColumn}
                        </Typography>
                    )}
                    {superlinkedColumn !== 'none' && (
                        <Typography variant="body2">
                            • <strong>Superlinked column:</strong>{' '}
                            {superlinkedColumn}
                        </Typography>
                    )}
                    {modifyAnalyzedText !== 'none' && (
                        <Typography variant="body2">
                            • <strong>Build graph from:</strong>{' '}
                            {modifyAnalyzedText === 'detectEntities'
                                ? 'Words and entities'
                                : modifyAnalyzedText === 'extractEntitiesOnly'
                                ? 'Only entities'
                                : modifyAnalyzedText === 'convertToEntities'
                                ? 'Cell text as entities'
                                : 'Words'}
                        </Typography>
                    )}
                    {replaceEntities && (
                        <Typography variant="body2">
                            •{' '}
                            <strong>Replace entities with root concepts</strong>
                        </Typography>
                    )}
                </Box>
            </Paper>
        </Box>
    )

    const renderStepContent = step => {
        switch (step) {
            case 0:
                return renderUploadStep()
            case 1:
                return renderFieldSelectionStep()
            case 2:
                return renderFilterSelectionStep()
            case 3:
                return renderConfigurationStep()
            default:
                return null
        }
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle
                sx={{
                    padding: 4,
                    color: theme => theme.palette.text.primary,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                Import JSON / JSON Lines
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {String(error)}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {String(success)}
                    </Alert>
                )}

                <Stepper
                    activeStep={activeStep}
                    sx={{ paddingTop: 2, paddingBottom: 2 }}
                >
                    {steps.map(label => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {renderStepContent(activeStep)}
            </DialogContent>
            <DialogActions
                sx={{
                    paddingRight: 4,
                    paddingLeft: 4,
                    paddingBottom: 4,
                    paddingTop: 2,
                }}
            >
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Box sx={{ flex: '1 1 auto' }} />
                {activeStep > 0 && (
                    <Button
                        onClick={handleBack}
                        disabled={loading}
                        variant="outlined"
                        sx={{ mr: 1, px: 3, py: 1, fontSize: '0.95rem' }}
                    >
                        Back
                    </Button>
                )}
                {activeStep === steps.length - 1 ? (
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        size="large"
                        disabled={!canProceedToNext() || loading}
                        startIcon={loading ? null : <CheckCircle />}
                        sx={{ px: 4, py: 1, fontSize: '0.95rem' }}
                    >
                        {loading ? 'Importing...' : 'Import'}
                    </Button>
                ) : (
                    <Button
                        onClick={handleNext}
                        variant="contained"
                        size="large"
                        disabled={!canProceedToNext()}
                        sx={{ px: 4, py: 1, fontSize: '0.95rem' }}
                    >
                        Next
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}
